import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _get from "@babel/runtime/helpers/esm/get";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";
import { warn as _warn } from "@instructure/console";

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { decorator } from '@instructure/ui-decorator';

/**
 * ---
 * category: utilities/react
 * ---
 * Flag React component props as hack props.
 * Warnings will display in the console when hack props are used.
 *
 * ```js
 *  class Example extends Component {
 *    static propTypes = {
 *      currentProp: PropTypes.func
 *    }
 *  }
 *  export default hack(['hackProp'])(Example)
 * ```
 *
 * @module hack
 * @param {array} hackProps
 * @param {string} message
 * @return {function} React component flagged as having hack props
 */
var hack = process.env.NODE_ENV == 'production' ? function () {
  return function (Component) {
    return Component;
  };
} : decorator(function (ComposedComponent, hackProps, message) {
  return /*#__PURE__*/function (_ComposedComponent) {
    _inherits(HackComponent, _ComposedComponent);

    var _super = _createSuper(HackComponent);

    function HackComponent() {
      _classCallCheck(this, HackComponent);

      return _super.apply(this, arguments);
    }

    _createClass(HackComponent, [{
      key: "componentDidMount",
      value: function componentDidMount() {
        if (hackProps) {
          warnHackProps(ComposedComponent.displayName, this.props, hackProps, message);
        }

        if (_get(_getPrototypeOf(HackComponent.prototype), "componentDidMount", this)) {
          _get(_getPrototypeOf(HackComponent.prototype), "componentDidMount", this).call(this);
        }
      }
    }, {
      key: "componentDidUpdate",
      value: function componentDidUpdate(prevProps, prevState, prevContext) {
        if (hackProps) {
          warnHackProps(ComposedComponent.displayName, this.props, hackProps, message);
        }

        if (_get(_getPrototypeOf(HackComponent.prototype), "componentDidUpdate", this)) {
          _get(_getPrototypeOf(HackComponent.prototype), "componentDidUpdate", this).call(this, prevProps, prevState, prevContext);
        }
      }
    }]);

    return HackComponent;
  }(ComposedComponent);
});

function warnHackProps(displayName, props, hackProps) {
  var message = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : '';
  hackProps.forEach(function (hackProp) {
    /*#__PURE__*/

    /*#__PURE__*/
    _warn(typeof props[hackProp] === 'undefined', "[".concat(displayName, "] The `").concat(hackProp, "` prop is a temporary hack and will be removed in a future release. ").concat(message));
  });
}

export default hack;
export {
/**
 * ---
 * category: utilities/react
 * ---
 * Flag React component props as hack props.
 * Warnings will display in the console when hack props are used.
 *
 * ```js
 *  class Example extends Component {
 *    static propTypes = {
 *      currentProp: PropTypes.func
 *    }
 *  }
 *  export default hack(['hackProp'])(Example)
 * ```
 *
 * @module hack
 * @param {array} hackProps
 * @param {string} message
 * @return {function} React component flagged as having hack props
 */
hack };